import React, {
	createContext,
	useContext,
	ReactNode,
	useState,
	useMemo,
	useEffect,
	useCallback,
	SetStateAction,
	useRef,
} from 'react';
import { Moment } from 'moment';
import { arrayMove } from '@dnd-kit/sortable';
import {
	useQuery,
	useQueryClient,
} from 'react-query';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { Values } from '@shared/utils/objectEnums';
import {
	Currencies,
	FuelTypes,
	PortActionTypes,
	ContractCompletionTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { filterUnique } from '@shared/utils/array';
import { isPortCallCompletionDto } from '@shared/utils/portCallHelpers';
import { round } from '@shared/utils/math';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import type { GetVoyageItineraryResponse } from '@api/features/voyages/getVoyageItinerary';
import type { GetRobsFromPortCallResponse, RobAndBunkers } from '@api/features/vessels/getRobsFromPortCall';
import type { UpdateRobBatchRequest } from '@api/features/vessels/updateRobBatch';
import type { CreateItineraryEntryRequest } from '@api/features/ops/createItineraryEntry';
import type { PortCallActionProps } from '@api/models/port-call-action';
import type { UpdatePortCallRequest } from '@api/features/ops/updatePortCall';
import type {
	ItineraryPortCallDto,
	ItinerarySeaPassageDto,
} from '@client/screens/estimates/details/helpers/types';
import {
	createItineraryEntry,
	deleteItineraryEntry,
	deleteRobBunker,
	getRobsFromPortCall,
	getVoyageItinerary,
	reorderPortCall,
	updatePortCall,
	updateRob,
	updateRobBatch,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import getItineraryColumns from './utils/getItineraryColumns';
import {
	CommencementRobBunker,
	NullableItineraryBunkerRecord,
} from './utils/bunkerTabHelpers';

export type RobChange = { key: string; portCallId: number; newQuantity: number | null; forecasted?: boolean; };

type ItineraryContextType = {
	itinerary: GetVoyageItineraryResponse;
	refreshItinerary: () => Promise<void>;
	itineraryError: unknown;
	itineraryLoading: boolean;
	deletePortCall: (portCall: ItineraryPortCallDto) => Promise<void>;
	portCalls: (ItineraryPortCallDto & { key: string })[];
	seaPassages: ItinerarySeaPassageDto[];
	localItinerary: (ItineraryPortCallDto | ItinerarySeaPassageDto)[];
	setLocalItinerary: (itinerary: (ItineraryPortCallDto | ItinerarySeaPassageDto)[]) => void;
	viewMode: 'map' | 'bunker' | 'default';
	setViewMode: (type: 'map' | 'bunker' | 'default') => void;
	timeZoneFormat: TimeZoneFormat;
	setTimeZoneFormat: (format: TimeZoneFormat) => void;
	addItineraryEntry: ({
		portId,
		estimatedDepartureDate,
		actualDepartureDate,
		robs,
	}: {
		portId: number;
		estimatedDepartureDate?: Moment;
		actualDepartureDate?: Moment;
		robs: CreateItineraryEntryRequest['robs'];
	}) => Promise<void>;
	expandedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto | undefined;
	onSelectEntry: (entry: ItinerarySeaPassageDto | ItineraryPortCallDto | undefined) => void;
	voyageDetails: GetVoyageDetailsResponse;
	hasPendingRobChanges: boolean;
	onUpdateRob: (key: string, portCallId: number, newQuantity: number | null) => void;
	onUpdatePortCall: (field: string, value: any, newRow: ItineraryPortCallDto) => Promise<void>;
	movePortCall: (pcId: number, overId: number) => Promise<void>;
	columns: ReturnType<typeof getItineraryColumns>;
	refreshVoyageDetails: () => void;
	showCommencementLinking: boolean;
	setShowCommencementLinking: (value: boolean) => void;
	onSaveRob: (
		id: number | string | undefined,
		portCallActionId: number | null | undefined,
		rob: NullableItineraryBunkerRecord
	) => Promise<void>;
	saveRobChanges: (changes?: RobChange[]) => Promise<void>;
	fetchedRobs: GetRobsFromPortCallResponse | undefined;
	refreshFetchedRobs: () => void;
	fetchedRobsLoading: boolean;
	onDeleteRob: (id: number | string | undefined) => Promise<void>;
	onSaveFuelQueueItem: (
		portCallActionId: number | string | undefined | null,
		robBunkerId: number,
		fuelQueue: Array<CommencementRobBunker>,
	) => Promise<void>;
	onRemoveFuelQueueItem: (
		row: CommencementRobBunker,
		commencementAction?: RobAndBunkers | null,
	) => Promise<void>;
	onAddNewRob: (
		portCallActionId: number | null | undefined,
		values: NullableItineraryBunkerRecord,
		showError?: boolean,
	) => Promise<void>;
	tcInCompletionBunkers: {
		quantity: number;
		fuelGrade: Values<typeof FuelTypes>;
		pricePerTon: number;
	}[] | null;
	setEditingCargoPortId: React.Dispatch<React.SetStateAction<number | null>>;
	setEditingCargoId: React.Dispatch<React.SetStateAction<number | null>>;
	editingCargoPortId: number | null;
	editingCargoId: number | null;
	renderKey: number;
	setItineraryShouldUpdate: React.Dispatch<SetStateAction<boolean>>;
	expandedPortCallAction: ItineraryPortCallDto['actions'][number] | null;
	setExpandedPortCallAction: React.Dispatch<React.SetStateAction<ItineraryPortCallDto['actions'][number] | null>>;
	sortDirection: string;
	setSortDirection: React.Dispatch<React.SetStateAction<SortDirection>>;
	firstLoadPort: { id: number; name: string } | null;
	lastDischargePort: { id: number; name: string } | null;
};

interface ItineraryProviderProps {
	children: ReactNode;
}
type SortDirection = 'asc' | 'desc';
const { UTC, LocalTime } = TimeZoneFormat;

export const ItineraryProvider = ({
	children,
}: ItineraryProviderProps) => {
	const {
		voyageDetails,
		refreshVoyageDetails,
		fixtureCurrency,
		fixtureDetails,
		voyageCompletion,
		refreshDetails,
		refreshPnl,
		fixtureType,
	} = useVoyage();

	const savingRobRef = useRef(false);
	const queryClient = useQueryClient();

	const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
	const [expandedPortCallAction, setExpandedPortCallAction] = useState<ItineraryPortCallDto['actions'][number] | null>(null);

	const [viewMode, setViewMode] = useState<'default' | 'map' | 'bunker'>('default');
	const [timeZoneFormat, setTimeZoneFormat] = useState<TimeZoneFormat>(
		fixtureDetails?.selectedTimeFormat ?? (voyageDetails.itineraryUseUTC ? UTC : LocalTime),
	);
	const [showCommencementLinking, setShowCommencementLinking] = useState(false);
	const [renderKey, setRenderKey] = useState(0);
	const [itineraryShouldUpdate, setItineraryShouldUpdate] = useState(false);
	const [editingCargoId, setEditingCargoId] = useState<number | null>(null);
	const [editingCargoPortId, setEditingCargoPortId] = useState<number | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

	const [pendingRobChanges, setPendingRobChanges] = useState<Array<{
		key: string;
		portCallId: number;
		newQuantity: number | null;
		forecasted?: boolean;
	}>>([]);

	const forceRender = () => setRenderKey((prev) => prev + 1);

	const {
		data: serverItinerary,
		refetch,
		error: itineraryError,
		isLoading: itineraryLoading,
	} = useQuery(
		['itinerary', voyageDetails.id],
		() => getVoyageItinerary(voyageDetails.id),
		{
			enabled: voyageDetails != null,
			refetchOnWindowFocus: false,
			retry: false,
			initialData: [],
			onSuccess: () => setItineraryShouldUpdate(true),
		},
	);

	const refreshItinerary = useCallback(async (shouldForceRender: boolean = true) => {
		await refetch();
		await queryClient.invalidateQueries(['vendorExpenses', voyageDetails.id]);

		if (shouldForceRender) {
			forceRender();
		}

		setItineraryShouldUpdate(true);
		queryClient.invalidateQueries(['voyageDetails', voyageDetails.id]);
		queryClient.invalidateQueries(['robs']);
	}, [refetch, queryClient, voyageDetails.id]);

	const itinerary = serverItinerary as GetVoyageItineraryResponse;

	const [
		localItinerary,
		setLocalItinerary,
	] = useState<(ItineraryPortCallDto | ItinerarySeaPassageDto)[]>(itinerary);

	useEffect(() => {
		setTimeZoneFormat(fixtureDetails?.selectedTimeFormat ?? (voyageDetails.itineraryUseUTC ? UTC : LocalTime));
	}, [fixtureDetails?.selectedTimeFormat, voyageDetails.itineraryUseUTC]);

	const rawPortCalls = useMemo(
		() => localItinerary.filter((e): e is ItineraryPortCallDto => isItemPortCall(e)),
		[localItinerary],
	);

	const firstLoadPort = useMemo(() => {
		const pc = rawPortCalls.find((p) =>
			(p.PortCallActions ?? []).some((a) => a.event === PortActionTypes.LOADING)
		);

		return pc?.port ?? null;
	}, [rawPortCalls]);

	const lastDischargePort = useMemo(() => {
		const reversed = [...rawPortCalls].reverse();

		const pc = reversed.find((p) =>
			(p.PortCallActions ?? []).some((a) => a.event === PortActionTypes.DISCHARGING)
		);

		return pc?.port ?? null;
	}, [rawPortCalls]);

	const seaPassages = useMemo(
		() => localItinerary.filter((e): e is ItinerarySeaPassageDto => !isItemPortCall(e)),
		[localItinerary],
	);

	// Creates columns like VLSFO-arrival & VLSFO-departure for each unique fuel type
	const portCalls = useMemo(() => {
		return rawPortCalls.map((pc, index) => {
			const transformed = pc.PortCallActions.reduce(
				(acc: Record<string, number>, item) => {
					let eventKey = item.event.toLowerCase();

					if (
						eventKey === PortActionTypes.COMMENCEMENT.toLowerCase() ||
						eventKey === PortActionTypes.COMPLETION.toLowerCase()
					) {
						eventKey = PortActionTypes.DEPARTURE.toLowerCase();
					}

					item.RobBunkers.forEach((bunker) => {
						const key = `${bunker.fuelGrade}-${eventKey}`;
						const quantity =
							bunker.forecasted ?
								round(bunker.forecastedQuantity, 3) :
								round(bunker.totalQuantity, 3);

						acc[key] = (acc[key] ?? 0) + quantity;
					});

					return acc;
				},
				{},
			);

			const pendingValues =
				Object.fromEntries(
					pendingRobChanges
						.filter((change) => change.portCallId === pc.id)
						.map((change) => [change.key, change.newQuantity]),

				);

			return {
				...pc,
				...transformed,
				...pendingValues,
				key: `${pc.port.id}-${index}?isCanalTransit=${pc.isCanalTransit}&id=${pc.id}&isActualDate=${pc.arrivalDate != null}`,
			};
		});
	}, [rawPortCalls, pendingRobChanges]);

	const sortedPortCalls = useMemo(() => {
		if (sortDirection === 'desc') {
			return [...portCalls].reverse();
		}

		return portCalls;
	}, [portCalls, sortDirection]);

	useEffect(() => {
		forceRender();
	}, [sortDirection]);

	const uniqueFuelTypes = useMemo(() => filterUnique(
		portCalls.flatMap((event) => (
			event.PortCallActions.flatMap((rob) => rob.RobBunkers.map((bunker) => bunker.fuelGrade))
		)),
		(fuelGrade) => fuelGrade,
	).sort(), [portCalls]);

	const expandedEntry = useMemo(() => {
		if (selectedIndex == null) {
			return undefined;
		}

		const selectedEntry = itinerary[selectedIndex];

		if (selectedEntry == null) {
			setItineraryShouldUpdate(true);

			return undefined;
		}

		return selectedEntry;
	}, [selectedIndex, itinerary]);

	const {
		data: fetchedRobs,
		refetch: refreshFetchedRobs,
		isLoading: fetchedRobsLoading,
	} = useQuery(
		['fetchedRobs', expandedEntry?.id],
		() => getRobsFromPortCall(voyageDetails.vesselId, expandedEntry?.id!),
		{
			enabled: expandedEntry != null && isItemPortCall(expandedEntry),
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	useEffect(() => {
		if (itinerary != null && itineraryShouldUpdate) {
			setLocalItinerary(itinerary);
			setItineraryShouldUpdate(false);
			queryClient.invalidateQueries('vcPnl');
			queryClient.invalidateQueries('tcPnl');
		}
	}, [itinerary, localItinerary, itineraryShouldUpdate, queryClient]);

	useEffect(() => {
		if (itinerary != null && itinerary.length > 0 && localItinerary.length === 0) {
			setLocalItinerary(itinerary);
			forceRender();
		}
	}, [itinerary, localItinerary.length]);

	const deletePortCall = useCallback(async (portCall: ItineraryPortCallDto) => {
		try {
			await deleteItineraryEntry(portCall.vesselId, portCall.id);
			await refreshItinerary();
			queryClient.invalidateQueries(['robs']);
			setItineraryShouldUpdate(true);
		} catch (e) {
			showErrorNotification('Could not delete port call', e as Error);
		}
	}, [refreshItinerary]);

	const addItineraryEntry = async ({
		portId,
		estimatedDepartureDate,
		actualDepartureDate,
		robs,
	}: {
		portId: number;
		estimatedDepartureDate?: Moment;
		actualDepartureDate?: Moment;
		robs: CreateItineraryEntryRequest['robs'];
	}) => {
		await createItineraryEntry({
			vesselId: Number(voyageDetails.vesselId),
			portId,
			savingIndicator: true,
			voyageId: voyageDetails.id,
			estimatedDepartureDate,
			actualDepartureDate,
			robs,
		});

		await refreshItinerary();
		await refreshPnl();
		setItineraryShouldUpdate(true);
	};

	const onAddNewRob = async (
		portCallActionId: number | null | undefined,
		values: NullableItineraryBunkerRecord,
		showError?: boolean,
	) => {
		if (voyageDetails == null || portCallActionId == null) {
			return;
		}

		if (values.quantity < 0) {
			showErrorNotification('Bunker quantity cannot be less than 0');

			return;
		}

		await updateRob({
			vesselId: voyageDetails.vesselId,
			voyageId: voyageDetails.id,
			portCallActionId,
			currency: fixtureCurrency,
			attributes: {
				remainingOnBoard: [{
					fuelGrade: values.fuelGrade,
					quantity: values.quantity,
					pricePerTon: values.pricePerTon ?? 0,
				}],
			},
			deleteMissingEntries: false,
			showError,
		});

		refreshFetchedRobs();
		refreshDetails();
		refreshPnl();
		setItineraryShouldUpdate(true);
	};

	const resolveQuantityForSave = (
		robBunker: { forecastedQuantity?: number | null },
		newQuantity: number | null,
	) => {
		// Because we allow 0 as a value
		if (newQuantity !== null) {
			return newQuantity;
		}

		// If the field is empty, revert back to estimatedQuantity, otherwise 0
		return robBunker.forecastedQuantity ?? 0;
	};

	const updateLocalRobState = useCallback((
		key: string,
		portCallId: number,
		newQuantity: number | null,
	) => {
		let [fuelType, event] = key.split('-');
		const portCall = serverItinerary
			?.find((pc) => pc.id === portCallId && isItemPortCall(pc)) as ItineraryPortCallDto;

		if (portCall == null) {
			return null;
		}

		if (
			event === PortActionTypes.DEPARTURE &&
			isPortCallCompletionDto(portCall)
		) {
			event = PortActionTypes.COMPLETION;
		}

		const rob = portCall.PortCallActions.find((r) => r.event.toLowerCase() === event.toLowerCase());

		if (rob == null) {
			return null;
		}

		const robBunker = rob.RobBunkers.find((b) => b.fuelGrade === fuelType);
		const resolved = robBunker == null ? (newQuantity ?? 0) : resolveQuantityForSave(robBunker, newQuantity);

		if (robBunker != null && robBunker.totalQuantity === resolved) {
			return null;
		}

		const updatedLocalItinerary = localItinerary.map((entry) => {
			if (entry.id !== portCallId || !isItemPortCall(entry)) {
				return entry;
			}

			const updatedRobs = entry.PortCallActions.map((robItem) => {
				if (robItem.id !== rob!.id) {
					return robItem;
				}

				const resolvedQuantity =
					robBunker == null ?
						(newQuantity ?? 0) :
						resolveQuantityForSave(robBunker, newQuantity);

				const updatedRobBunkers = robItem.RobBunkers.map((bunker) => {
					if (bunker.id !== robBunker?.id) {
						return bunker;
					}

					return {
						...bunker,
						totalQuantity: resolvedQuantity,
					};
				});

				return {
					...robItem,
					RobBunkers: updatedRobBunkers,
				};
			});

			return {
				...entry,
				PortCallActions: updatedRobs as Array<PortCallActionProps>,
			};
		});

		// @ts-ignore
		setLocalItinerary(updatedLocalItinerary);

		return { portCall, rob, robBunker, fuelType };
	}, [localItinerary, serverItinerary]);

	const saveRobChanges = useCallback(async (changes?: RobChange[]) => {
		if (savingRobRef.current) {
			return;
		}

		const toSave = changes ?? pendingRobChanges;

		if (!Array.isArray(toSave)) {
			return;
		}

		savingRobRef.current = true;

		try {
			const updates: UpdateRobBatchRequest['updates'] = [];
			const sortedChanges = [...toSave].sort((a, b) => (
				portCalls.findIndex((pc) => pc.id === a.portCallId) - portCalls.findIndex((pc) => pc.id === b.portCallId)
			));

			sortedChanges.forEach((change) => {
				const result = updateLocalRobState(change.key, change.portCallId, change.newQuantity);

				if (!result) {
					return;
				}

				const { portCall, rob, robBunker, fuelType } = result;

				if (portCall.voyageId == null) {
					return;
				}

				if (robBunker == null && fuelType != null) {
					updates.push({
						portCallActionId: rob.id,
						attributes: {
							remainingOnBoard: [{
								fuelGrade: fuelType as Values<typeof FuelTypes>,
								quantity: change.newQuantity ?? 0,
								pricePerTon: 0,
							}],
						},
						forecasted: change.forecasted,
						currency: voyageDetails?.bankAccount?.currency ?? Currencies.USD,
						deleteMissingEntries: false,
					});
				} else if (robBunker != null) {
					const finalQty = resolveQuantityForSave(robBunker, change.newQuantity);
					updates.push({
						portCallActionId: rob.id,
						forecasted: change.forecasted,
						attributes: {
							remainingOnBoard: {
								id: robBunker.id,
								quantity: finalQty,
								fuelGrade: robBunker.fuelGrade,
							},
						},
						currency: voyageDetails?.bankAccount?.currency ?? Currencies.USD,
					});
				}
			});

			if (updates.length > 0) {
				await updateRobBatch(voyageDetails.vesselId, voyageDetails.id, updates, true);
				setPendingRobChanges([]);
				await refreshItinerary(false);
				refreshPnl();
			}
		} catch (e) {
			console.error(e);
			showErrorNotification('Failed to update ROB values', e as Error);
		} finally {
			savingRobRef.current = false;
		}
	}, [
		pendingRobChanges,
		updateLocalRobState,
		voyageDetails,
		refreshItinerary,
		refreshPnl,
	]);

	const onUpdateRob = useCallback((
		key: string,
		portCallId: number,
		newQuantity: number | null,
	) => {
		setPendingRobChanges((prevChanges) => {
			const filteredChanges = prevChanges
				.filter((change) => !(change.key === key && change.portCallId === portCallId));

			return [...filteredChanges, { key, portCallId, newQuantity }];
		});
	}, []);

	const onUpdatePortCallAttributes = useCallback(async (
		attributes: Omit<UpdatePortCallRequest['attributes'], 'PortCallActions'>,
		newRow: ItineraryPortCallDto,
	) => {
		const newLocalItinerary = localItinerary.map((entry) => {
			if (entry.id === newRow.id && isItemPortCall(entry)) {
				return {
					...entry,
					...attributes,
				};
			}

			return entry;
		});

		setLocalItinerary(newLocalItinerary);

		try {
			await updatePortCall(newRow.vesselId, newRow.id, attributes, true);
		} catch (e: any) {
			showErrorNotification(e.toString());
		}

		await refreshItinerary();
		await refreshPnl();
		await refreshVoyageDetails();
		setItineraryShouldUpdate(true);
	}, [refreshItinerary, localItinerary, refreshPnl, refreshVoyageDetails]);

	const onUpdatePortCall = useCallback((
		field: string,
		value: any,
		newRow: ItineraryPortCallDto,
	) => onUpdatePortCallAttributes({ [field]: value }, newRow), [onUpdatePortCallAttributes]);

	const onDeleteRob = async (id: number | string | undefined) => {
		if (id == null || voyageDetails?.vesselId == null) {
			return;
		}

		await deleteRobBunker(Number(id), voyageDetails?.vesselId);
		refreshFetchedRobs();
		refreshDetails();
		setItineraryShouldUpdate(true);
	};

	const movePortCall = useCallback(async (pcId: number, overId: number) => {
		const overEntry = portCalls.find((p) => p.id === overId);
		const activeEntry = portCalls.find((p) => p.id === pcId);
		const voyageId = activeEntry?.voyageId;

		if (pcId !== overId && overEntry != null && activeEntry != null && voyageId != null) {
			const activeIndex = portCalls.findIndex((i) => i.id === pcId);
			const overIndex = portCalls.findIndex((i) => i.id === overId);

			const oldItinerary = [...localItinerary];
			const updated = arrayMove(portCalls, activeIndex, overIndex);
			setLocalItinerary(updated);
			forceRender();

			try {
				await reorderPortCall({
					vesselId: Number(activeEntry?.vesselId),
					draggedId: activeEntry.id,
					droppedOverId: overEntry.id,
					voyageId,
				});
				await refreshItinerary();
			} catch (e) {
				setLocalItinerary(oldItinerary);
				showErrorNotification('Could not move port call', e as Error);
			}
		}

		setItineraryShouldUpdate(true);
	}, [localItinerary, portCalls, refreshItinerary]);

	const onSelectEntry = useCallback((
		entry: ItineraryPortCallDto | ItinerarySeaPassageDto | undefined,
	) => {
		if (entry == null) {
			setSelectedIndex(undefined);
			setItineraryShouldUpdate(true);

			return;
		}

		const isPortCall = isItemPortCall(entry);
		const index = itinerary.findIndex((e) => (
			e.id === entry.id && isItemPortCall(e) === isPortCall
		));
		setSelectedIndex(index);
	}, [itinerary]);

	const onSaveRob = async (
		id: number | string | undefined,
		portCallActionId: number | null | undefined,
		rob: NullableItineraryBunkerRecord,
	) => {
		if (voyageDetails == null || portCallActionId == null) {
			return;
		}

		if (rob.quantity < 0) {
			showErrorNotification('Bunker quantity cannot be less than 0');

			return;
		}

		await updateRob({
			vesselId: voyageDetails.vesselId,
			voyageId: voyageDetails.id,
			portCallActionId,
			currency: fixtureCurrency,
			attributes: {
				remainingOnBoard: {
					id: Number(id),
					quantity: rob.quantity,
					fuelGrade: rob.fuelGrade,
					pricePerTon: rob.pricePerTon ?? 0,
				},
			},
		});

		await refreshItinerary();
		refreshPnl();
		setItineraryShouldUpdate(true);
	};

	// Only relevant for commencement where users can add multiple of the same fuel type
	const onRemoveFuelQueueItem = async (
		row: CommencementRobBunker,
		commencementAction?: RobAndBunkers | null,
	) => {
		if (row == null || voyageDetails == null) {
			return;
		}

		const relevantRob = commencementAction?.RobBunkers
			.find((rb) => rb.id === row.robBunkerId);

		if (relevantRob == null) {
			showErrorNotification('Could not remove bunker');

			return;
		}

		const newFuelQueue = relevantRob?.fuelQueue
			.filter((fq) => fq.pricePerTon !== row.pricePerTon && fq.quantity !== row.quantity);

		await updateRob({
			vesselId: voyageDetails.vesselId,
			voyageId: voyageDetails.id,
			portCallActionId: row.portCallActionId,
			robBunkerId: row.robBunkerId,
			currency: fixtureCurrency,
			attributes: {
				remainingOnBoard: newFuelQueue,
			},
			resetQueue: true,
			event: PortActionTypes.COMMENCEMENT,
		});

		await refreshFetchedRobs();
		await refreshDetails();
		refreshPnl();
		setItineraryShouldUpdate(true);
	};

	// Only relevant for commencement where users can add multiple of the same fuel type
	const onSaveFuelQueueItem = async (
		portCallActionId: number | string | undefined | null,
		robBunkerId: number,
		fuelQueue: Array<CommencementRobBunker>,
	) => {
		if (fuelQueue == null || voyageDetails == null || portCallActionId == null) {
			return;
		}

		await updateRob({
			vesselId: voyageDetails.vesselId,
			voyageId: voyageDetails.id,
			portCallActionId: Number(portCallActionId),
			robBunkerId,
			currency: fixtureCurrency,
			attributes: {
				remainingOnBoard: fuelQueue,
			},
			resetQueue: true,
			event: PortActionTypes.COMMENCEMENT,
		});

		await refreshFetchedRobs();
		await refreshDetails();
		refreshPnl();

		setItineraryShouldUpdate(true);
	};

	const tcInCompletionBunkers = useMemo(() => {
		if (expandedEntry == null || !isItemPortCall(expandedEntry) || voyageCompletion == null) {
			return [];
		}

		const isTcInDelivery = isPortCallCompletionDto(expandedEntry) &&
			voyageCompletion.completionType === ContractCompletionTypes.TC_IN_DELIVERY;

		const result = isTcInDelivery ? (voyageCompletion.linkedTcInVoyageBunkers ?? []).filter((r) => r.type === 'redelivery')
			.map((b) => ({
				quantity: b.quantity,
				fuelGrade: b.fuelGrade,
				pricePerTon: b.pricePerTon,
			})) : null;

		return result;
	}, [expandedEntry, voyageCompletion]);

	const columns = useMemo(() => getItineraryColumns({
		deletePortCall,
		onSelectEntry,
		portCalls,
		seaPassages,
		onUpdatePortCall,
		onUpdatePortCallAttributes,
		fixtureType,
		viewMode,
		setShowCommencementLinking,
		setEditingCargoId,
		setEditingCargoPortId,
		uniqueFuelTypes,
		timeZoneFormat,
		saveRobChanges,
		isBeforeMinPeriod: voyageDetails.isBeforeMinPeriod ?? false,
		isBeyondMaxPeriod: voyageDetails.isBeyondMaxPeriod ?? false,
	}), [
		deletePortCall,
		onSelectEntry,
		portCalls,
		seaPassages,
		onUpdatePortCall,
		onUpdatePortCallAttributes,
		fixtureType,
		viewMode,
		uniqueFuelTypes,
		timeZoneFormat,
		onUpdateRob,
		saveRobChanges,
		voyageDetails.isBeforeMinPeriod,
		voyageDetails.isBeyondMaxPeriod,
	]);

	return (
		<ItineraryContext.Provider value={{
			tcInCompletionBunkers,
			itinerary,
			localItinerary,
			refreshItinerary,
			itineraryError,
			itineraryLoading,
			deletePortCall,
			portCalls: sortedPortCalls,
			seaPassages,
			setLocalItinerary,
			setViewMode,
			viewMode,
			timeZoneFormat,
			showCommencementLinking,
			setShowCommencementLinking,
			setTimeZoneFormat,
			setItineraryShouldUpdate,
			addItineraryEntry,
			hasPendingRobChanges: pendingRobChanges.length > 0,
			expandedEntry,
			voyageDetails,
			onSelectEntry,
			onUpdateRob,
			onUpdatePortCall,
			movePortCall,
			columns,
			refreshVoyageDetails,
			onSaveRob,
			saveRobChanges,
			fetchedRobs,
			refreshFetchedRobs,
			fetchedRobsLoading,
			onDeleteRob,
			onAddNewRob,
			onRemoveFuelQueueItem,
			onSaveFuelQueueItem,
			editingCargoId,
			editingCargoPortId,
			setEditingCargoPortId,
			setEditingCargoId,
			renderKey,
			expandedPortCallAction,
			setExpandedPortCallAction,
			sortDirection,
			setSortDirection,
			firstLoadPort,
			lastDischargePort,
		}}
		>
			{children}
		</ItineraryContext.Provider>
	);
};

const ItineraryContext = createContext<ItineraryContextType>(undefined!);
export const useItinerary = () => useContext(ItineraryContext);
